export interface IProductCategory {
  id: number;
  name: string;
  slug: string;
  external_id: string | null;
  parent_id: number | null;
  order: number;
  attribute_values?: Array<{ id: number; name: string }>;
}

export interface ParentOption {
  id: number;
  label: string;
}

// Builds the parent <select> options from the flat category list, excluding the
// category currently being edited (so it can't be set as its own parent). The
// label is the category's full path ("Parent / Child") so admins can tell apart
// repeated leaf names across the tree.
export function buildParentOptions(
  list: IProductCategory[],
  excludeId?: number,
): ParentOption[] {
  const byId = new Map(list.map((c) => [c.id, c]));

  const pathFor = (cat: IProductCategory): string => {
    const parts: string[] = [cat.name];
    let current = cat;
    const guard = new Set<number>([cat.id]);
    while (current.parent_id != null) {
      const parent = byId.get(current.parent_id);
      if (!parent || guard.has(parent.id)) break;
      parts.unshift(parent.name);
      guard.add(parent.id);
      current = parent;
    }
    return parts.join(' / ');
  };

  return list
    .filter((c) => c.id !== excludeId)
    .map((c) => ({ id: c.id, label: pathFor(c) }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function parentName(
  list: IProductCategory[],
  parentId: number | null,
): string {
  if (parentId == null) return '—';
  return list.find((c) => c.id === parentId)?.name ?? '—';
}

export interface TreeRow {
  cat: IProductCategory;
  depth: number;
  hasChildren: boolean;
}

// Flattens the category tree into the ordered list of *visible* rows: a node's
// children appear only when the node is in `expanded`. Each row carries its
// depth (for indentation) and whether it has children (for the toggle). Nodes
// whose parent_id is missing from the list are treated as roots so nothing is
// hidden by dangling references.
export function buildVisibleRows(
  list: IProductCategory[],
  expanded: Set<number>,
): TreeRow[] {
  const idSet = new Set(list.map((c) => c.id));
  const childrenByParent = new Map<number | null, IProductCategory[]>();
  list.forEach((c) => {
    const key = c.parent_id != null && idSet.has(c.parent_id) ? c.parent_id : null;
    if (!childrenByParent.has(key)) childrenByParent.set(key, []);
    (childrenByParent.get(key) as IProductCategory[]).push(c);
  });
  childrenByParent.forEach((arr) =>
    arr.sort((a, b) => a.order - b.order || a.name.localeCompare(b.name)),
  );

  const rows: TreeRow[] = [];
  const walk = (parentId: number | null, depth: number) => {
    const kids = childrenByParent.get(parentId) || [];
    kids.forEach((cat) => {
      const hasChildren = (childrenByParent.get(cat.id) || []).length > 0;
      rows.push({ cat, depth, hasChildren });
      if (hasChildren && expanded.has(cat.id)) walk(cat.id, depth + 1);
    });
  };
  walk(null, 0);
  return rows;
}

// Every category id that has at least one child — used for "expand all".
export function parentIds(list: IProductCategory[]): number[] {
  const withChildren = new Set<number>();
  const idSet = new Set(list.map((c) => c.id));
  list.forEach((c) => {
    if (c.parent_id != null && idSet.has(c.parent_id)) withChildren.add(c.parent_id);
  });
  return [...withChildren];
}
